
import bindAllOfThis from 'src/utils/BindThisHelper';
import { TiledGameObject } from 'src/games/shared/phaserHelpers/HandyTypes';
import TranslateApi from 'src/utils/TranslateHelper';
import { TrayState } from '../objects/TrayState';
import { GridTile, MoveableCityTile, ZoneType, StaticCityTile } from '../objects/GridTile';
import { EndGameScene } from './EndGameScene';

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    state: TrayState;
    grid: GridTile[][];
    tray: Phaser.GameObjects.Sprite;

    staticTiles: StaticCityTile[];
    moveableTiles: MoveableCityTile[];

    buttonIndicator: Phaser.GameObjects.Image;

    GRID_HEIGHT: number = 5;
    GRID_WIDTH: number = 13;
    GRID_TILE_SIZE: number = 100;

    feedback: Phaser.GameObjects.Container;

    zones: Phaser.GameObjects.Image[];

    // These are the two columns which need to be checked if attempting to drop a 200x200 CityTile on a GridTile in either of these columns
    // Because the left side and the right side will not be in the same zone
    PROBLEM_COL_1 = 3;
    PROBLEM_COL_2 = 8;

    constructor() {
        super({ key: "MainScene" });
        bindAllOfThis(this, MainScene.prototype);
    }

    preload() {
        this.load.tilemapTiledJSON("map", require("src/assets/qci/maps/city_builder.json"));
        this.load.image("background", require("src/assets/qci/backgrounds/1i_background.png"));
        this.load.atlas("assets", require("src/assets/qci/atlas/gameCityBuilder.png"), require("src/assets/qci/atlas/gameCityBuilder.json"));
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: "map" });
        this.grid = [];
        for (var row = 0; row < this.GRID_HEIGHT; row++) {
            this.grid[row] = [];
        }
        this.staticTiles = [];
        this.moveableTiles = [];
        this.state = TrayState.ROADS;
    }

    create() {
        this.initialize();
        var background = this.add.image(0, 0, "background").setOrigin(0);

        var commercialZone = this.add.image(0, 0, "assets", "commercialZone");
        commercialZone.setOrigin(0).setName("commercial");
        var residentialZone = this.add.image(0, 0, "assets", "residentialZone");
        residentialZone.setOrigin(0).setName("residential");
        var industrialZone = this.add.image(0, 0, "assets", "industrialZone");
        industrialZone.setOrigin(0).setName("industrial");

        this.zones = [commercialZone, residentialZone, industrialZone];

        this.buildTrayArea();
        this.buildGrid();
        this.registerEvents();
    }

    registerEvents() {
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, tile: MoveableCityTile, dragX: number, dragY: number) => {
            pointer.x = tile.x + 30;
            pointer.y = tile.y - 30;
            this.children.bringToTop(tile);
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, tile: MoveableCityTile, dragX: number, dragY: number) => {
            tile.x = pointer.x - 30;
            tile.y = pointer.y + 30;
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, tile: MoveableCityTile, dropped: boolean) => {
            if (!dropped) {
                this.putBack(tile);
            }
        });

        this.input.on('drop', (pointer: Phaser.Input.Pointer, tile: MoveableCityTile, zone) => {
            if (zone.name === "tray") {
                this.putTileInTray(tile);
            }
            else {
                this.dropTile(tile, zone as GridTile);
            }
        });
    }

    buildTrayArea() {
        this.map.getObjectLayer('Tray').objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "tray") {
                this.tray = this.add.sprite(0, ob.y, "assets", "toolbar");
                this.tray.setY(this.gameHeight - this.tray.height);
                this.tray.setOrigin(0);
                this.tray.setName("tray");

                // Tray is also a dropzone
                this.tray.setInteractive();
                this.tray.input.dropZone = true;
            }
            else if (ob.name === "buttons") {
                this.createButtons(ob);
            }
            else if (ob.name === "items") {
                this.createCityTiles(ob, "roads");
                this.createCityTiles(ob, "commercial");
                this.createCityTiles(ob, "industrial");
                this.createCityTiles(ob, "residential");
            }
            else if (ob.name === "button") {
                var done = this.add.image(ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", "button");
                done.setInteractive({ useHandCursor: true });
                done.on("pointerdown", () => {
                    this.endGame();
                });

                var style = { fontFamily: "Russo One, Arial", fontSize: 20, align: "center" };
                var text = this.add.text(ob.x, ob.y, TranslateApi.translate("common.done"), style);
                Phaser.Display.Align.In.Center(text, done);
            }
        });

        this.showTiles("roads");
    }

    buildGrid() {
        var ob = this.map.getObjectLayer('Scene').objects.find((ob) => ob.name === "grid") as TiledGameObject;
        var x, y;
        var id = 0;

        for (var row = 0; row < this.GRID_HEIGHT; row++) {
            y = ob.y + (row * this.GRID_TILE_SIZE + this.GRID_TILE_SIZE / 2);
            this.grid[row] = [];
            for (var col = 0; col < this.GRID_WIDTH; col++) {
                x = ob.x + (col * this.GRID_TILE_SIZE + this.GRID_TILE_SIZE / 2);
                var tile = new GridTile(this, x, y, 100, 100, id);
                this.grid[row][col] = tile;
                tile.row = row;
                tile.col = col;
                id++;
            }
        }
    }

    createButtons(ob: TiledGameObject) {
        var buttons = [];
        for (var state of TrayState.TRAYSTATES) {
            var button = this.createButton(state);
            buttons.push(button);
        }

        Phaser.Actions.GridAlign(buttons, {
            x: ob.x + 100,
            y: ob.y + 20,
            width: 1,
            height: 4,
            cellHeight: 40,
            cellWidth: 150,
            position: Phaser.Display.Align.TOP_LEFT
        });

        this.buttonIndicator = this.add.image(45, 570, "assets", "arrow2");
    }

    createButton(state: TrayState): Phaser.GameObjects.Text {
        var style = { fontFamily: "Russo One, Arial", fontSize: 20, align: "left" };
        var button = this.add.text(0, 0, TranslateApi.translate("CityBuilder.Phaser." + state.type), style);
        button.setOrigin(0.5);
        button.setTint(state.color);
        button.setInteractive({ useHandCursor: true });
        button.on("pointerdown", () => {
            this.state = state;
            this.showTiles(state.type);
            this.buttonIndicator.setY(button.y);
        });

        return button;
    }

    createCityTiles(ob: TiledGameObject, tileType: ZoneType) {
        var id = 1;
        var num = tileType === "roads" ? 11 : tileType === "commercial" ? 5 : tileType === "industrial" ? 3 : tileType === "residential" ? 6 : 0;
        var y = this.tray.getCenter().y;
        var x = ob.x;

        for (var i = 0; i < num; i++) {
            var setY;
            if (tileType === "roads") {
                // Reset x to start second row
                if (i === 8) {
                    x = ob.x;
                }
                // y is different for top and bottom row
                if (i <= 7) {
                    setY = y;
                }
                else {
                    setY = y + 110;
                }
            }
            else {
                setY = y + 100;
            }

            var staticTile = new StaticCityTile(this, x, 0, "assets", id, tileType);
            staticTile.setY(setY);
            staticTile.updateOutline();
            this.staticTiles.push(staticTile);

            var tile = new MoveableCityTile(this, x, 0, "assets", id, tileType);
            tile.setY(setY);
            tile.setVisible(false);
            this.moveableTiles.push(tile);

            x += staticTile.displayWidth + 10;
            id++;
        }
    }

    dropTile(cityTile: MoveableCityTile, gridTile: GridTile) {
        if (cityTile.type === gridTile.type || cityTile.type === "roads") {
            if (cityTile.displayHeight === 200 && gridTile.row === 0) {
                // Case where the top part is off the screen - don't let them do it!
                this.putBack(cityTile);
                return;
            }

            // 200x200 Tile - 4x4 grid
            if (cityTile.displayWidth === 200 && cityTile.displayHeight === 200) {
                if (cityTile.type !== "roads" && (gridTile.col === this.PROBLEM_COL_1 || gridTile.col === this.PROBLEM_COL_2)) {
                    // Case where the right side falls into another zone - don't allow them to do it!
                    this.putBack(cityTile);
                    return;
                }
            }

            // Snap it to the bottom left of the gridTile, mark it as placed
            Phaser.Display.Align.In.BottomLeft(cityTile, gridTile);
            this.children.moveDown(cityTile);
            cityTile.placed = true;

            if (this.needMoveableTile(cityTile)) {
                // Add a new CityTile to replace this one and put it on top of the correct static tile in the tray
                var newMoveableTile = new MoveableCityTile(this, cityTile.x, cityTile.y, "assets", cityTile.id, cityTile.type);
                var staticTile = this.findMatchingStaticTile(cityTile);
                newMoveableTile.x = staticTile.x;
                newMoveableTile.y = staticTile.y;
                newMoveableTile.visible = newMoveableTile.type === this.state.type;
                this.moveableTiles.push(newMoveableTile);
            }
        }
        else {
            this.putBack(cityTile);
        }
    }

    needMoveableTile(tile: MoveableCityTile): boolean {
        return this.moveableTiles.find((t: MoveableCityTile) => !t.placed && t.type === tile.type && t.id === tile.id) == null;
    }

    /**
     * Put it back where it was picked up
     */
    putBack(tile: MoveableCityTile) {
        tile.x = tile.input.dragStartX;
        tile.y = tile.input.dragStartY;
    }

    putTileInTray(tile: MoveableCityTile) {
        var staticTile = this.findMatchingStaticTile(tile);
        tile.x = staticTile.x;
        tile.y = staticTile.y;
        tile.placed = false;

        if (this.state.type !== tile.type) {
            tile.setVisible(false);
        }
    }

    showTiles(type: ZoneType) {
        this.staticTiles.forEach((t: StaticCityTile) => {
            if (t.type === type) {
                t.visibility(true);
            }
            else {
                t.visibility(false);
            }
        });

        // The actual CityTiles are always showing if they're placed on the grid
        this.moveableTiles.forEach((t: MoveableCityTile) => {
            if (t.placed) {
                t.setVisible(true);
            }
            else {
                if (t.type === type) {
                    t.setVisible(true);
                }
                else {
                    t.setVisible(false);
                }
            }
        });

        this.zones.forEach((z) => {
            z.visible = type === "roads" || z.name === type;
        });
    }

    findMatchingStaticTile(cityTile: MoveableCityTile): StaticCityTile {
        return this.staticTiles.find((t: StaticCityTile) => t.type === cityTile.type && t.id === cityTile.id);
    }

    endGame() {
        this.input.removeAllListeners();
        this.scene.add("EndGameScene", EndGameScene, true);
        this.scene.remove(this);
    }
}



// WEBPACK FOOTER //
// ./src/games/CityBuilderGame/scenes/MainScene.ts